import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Box, Ruler, Maximize2 } from "lucide-react";
import StudyModal from "../StudyModal";
import Lightbox from "../Lightbox";
import { STUDIES, DELIVERABLES_NOTE } from "../../data/studies";
import "./StudiesSection.css";

const STEP_ICONS = [ClipboardList, Box, Ruler];

const StudyCard = ({ study, onOpen, onZoom, delay }) => (
  <article className={`st-card reveal ${study.hidePlan ? "st-card-document" : ""} ${study.featured ? "st-card-featured" : ""}`} data-delay={delay} data-testid={`study-card-${study.num}`} aria-labelledby={`st-title-${study.id}`}>
    <div className="st-hero">
      <span className="st-num">{study.num}</span>
      <button type="button" className="st-hero-btn" onClick={() => onZoom(study, "hero")} aria-label={`Agrandir : ${study.hero.label} — ${study.title}`} data-testid={`study-hero-zoom-${study.num}`}>
        <img src={study.hero.src} srcSet={study.hero.srcSet} sizes="(max-width: 640px) 92vw, (max-width: 1100px) 46vw, 400px" alt={study.hero.alt} loading="lazy" width={study.hero.width} height={study.hero.height} />
        <span className="st-zoom"><Maximize2 size={14} /></span>
      </button>
      <span className="st-hero-tags">
        <span className="demo-tag">{study.hero.label}</span>
        <span className="demo-tag st-tag-soft">{study.hero.note}</span>
      </span>
    </div>

    <div className="st-body">
      {study.featured && <p className="st-featured-label">À la une · étude illustrative</p>}
      <h3 id={`st-title-${study.id}`} className="st-title">{study.title}</h3>
      <p className="st-subtitle">{study.subtitle}</p>
      {study.visualWarning && <p className="st-visual-warning">{study.visualWarning}</p>}
      <p className="st-text">
        <strong>Besoin.</strong> {study.need}
      </p>
      <p className="st-text">
        <strong>{study.workLabel || "Travail SI2T."}</strong> {study.work}
      </p>
    </div>

    {!study.hidePlan && <div className="st-plan">
      {study.plan && (
        <button type="button" className="st-plan-btn" onClick={() => onZoom(study, "plan")} aria-label={`Agrandir l'aperçu du plan — ${study.title}`} data-testid={`study-plan-zoom-${study.num}`}>
          <img src={study.plan.src} srcSet={study.plan.srcSet} sizes="(max-width: 640px) 92vw, (max-width: 1100px) 46vw, 400px" alt={study.plan.alt} loading="lazy" width={study.plan.width} height={study.plan.height} />
          <span className="st-zoom"><Maximize2 size={14} /></span>
        </button>
      )}
      <p className="st-plan-caption">
        <span>{study.plan.label}</span>
        <span className="st-plan-note">{study.plan.note}</span>
      </p>
    </div>}

    <ol className={`st-steps ${study.hidePlan ? "st-steps-text" : ""}`} aria-label="Étapes de l'étude">
      {study.steps.map((s, i) => {
        const Icon = STEP_ICONS[i];
        const visual = s.visual !== null ? study.visuals[s.visual] : null;
        return (
          <li key={s.label} className="st-step">
            {!study.hidePlan && <span className="st-step-thumb">
              {visual ? <img src={visual.src} srcSet={visual.srcSet} sizes="96px" alt="" loading="lazy" width={visual.width} height={visual.height} /> : <Icon size={18} />}
            </span>}
            <span className="st-step-label">
              <span className="st-step-index">0{i + 1}</span>
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>

    <div className="st-deliverables">
      <span className="st-deliverables-label">{study.deliverablesLabel || "Livrables"}</span>
      <ul aria-label={study.deliverablesLabel || "Livrables"}>
        {study.deliverables.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>
    </div>

    <div className="st-foot">
      <button type="button" className="btn btn-primary st-cta" onClick={() => onOpen(study)} data-testid={`study-open-${study.num}`}>
        {study.hidePlan ? "Voir la fiche illustrée" : "Voir l'étude et ses livrables"}
        <ArrowRight size={16} strokeWidth={2.2} />
      </button>
    </div>
  </article>
);

const StudiesSection = () => {
  const [open, setOpen] = useState(null);
  const [zoom, setZoom] = useState(null);

  const onZoom = (study, which) => {
    const items = study.visuals.map((v) => ({ src: v.src, alt: v.alt, label: `${study.num} · ${v.label}`, note: v.note, caption: `${study.title} — ${study.subtitle}` }));
    const index = study.visuals.findIndex((v) => v.src === (which === "plan" ? study.plan?.src : study.hero.src));
    setZoom({ items, index: Math.max(index, 0) });
  };

  return (
    <section className="studies s-section" id="etudes" aria-labelledby="etudes-title" data-testid="studies-section">
      <div className="s-container">
        <div className="st-head">
          <div className="reveal">
            <p className="s-label">Études types</p>
            <h2 id="etudes-title" className="s-title">Du besoin au dossier de fabrication</h2>
            <p className="s-lead">
              Conception, modélisation et mise en plan : découvrez le travail et les livrables associés à chaque étude.
            </p>
          </div>
          <Link to="/galerie" className="arrow-link reveal" data-delay="1" data-testid="studies-gallery-link">
            Voir la galerie
            <ArrowRight size={15} strokeWidth={2.2} />
          </Link>
        </div>

        <div className="st-grid">
          {STUDIES.map((study, i) => (
            <StudyCard key={study.id} study={study} delay={i + 1} onOpen={setOpen} onZoom={onZoom} />
          ))}
        </div>

        <p className="st-note reveal" data-testid="studies-note">
          Études types : rendus CAO et aperçus de plans illustratifs, non validés pour la fabrication. {DELIVERABLES_NOTE}
        </p>
      </div>

      {open && <StudyModal study={open} onClose={() => setOpen(null)} />}
      {zoom && <Lightbox items={zoom.items} index={zoom.index} onClose={() => setZoom(null)} />}
    </section>
  );
};

export default StudiesSection;
